import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Shield,
  Eye,
  Database,
  Lock,
  UserCheck,
  Clock,
  Baby,
  RefreshCw,
  Mail,
  ArrowLeft,
  MapPin,
  BarChart3,
  Share2,
  FileText,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { updateSEO, buildBreadcrumbSchema } from "../utils/seo";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5 },
  }),
};

const sections = [
  {
    icon: Eye,
    color: "text-blue-600",
    bg: "bg-blue-50",
    number: "01",
    title: "Introduction",
    content:
      "SaferSavvy is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our web and mobile applications and services.",
  },
  {
    icon: Database,
    color: "text-indigo-600",
    bg: "bg-indigo-50",
    number: "02",
    title: "Information We Collect",
    content: "",
    subsections: [
      {
        subtitle: "Personal Information",
        text: "We may collect personal information that you provide to us, including:",
        bullets: [
          "Name, email address, and phone number",
          "CNIC (Computerized National Identity Card) number",
          "Profile pictures and identification documents",
          "Department and student/driver information",
          "Bus route preferences and travel history",
        ],
      },
      {
        subtitle: "Location Data",
        text: "With your permission, we collect real-time location data to provide bus tracking services, route optimization, and safety features. Location data is collected only when the app is in use and can be disabled in your device settings.",
        icon: MapPin,
      },
      {
        subtitle: "Usage Data",
        text: "We automatically collect information about how you interact with our app, including:",
        bullets: [
          "Device information (model, operating system, unique identifiers)",
          "App usage patterns and features accessed",
          "Error logs and performance data",
        ],
        icon: BarChart3,
      },
    ],
  },
  {
    icon: FileText,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    number: "03",
    title: "How We Use Your Information",
    content: "We use the collected information for the following purposes:",
    bullets: [
      "To provide and maintain our bus transportation services",
      "To track bus locations and provide real-time updates",
      "To manage user accounts and verify identities",
      "To send notifications about bus schedules and route changes",
      "To improve our services and develop new features",
      "To ensure safety and security of our transportation system",
      "To comply with legal obligations and university policies",
    ],
  },
  {
    icon: Share2,
    color: "text-orange-600",
    bg: "bg-orange-50",
    number: "04",
    title: "Data Sharing & Disclosure",
    content:
      "We do not sell your personal information. We may share your information only in the following circumstances:",
    bullets: [
      "With university administration for transportation management",
      "With bus drivers and transport administrators for operational purposes",
      "When required by law or to protect our rights and safety",
      "With service providers who assist in app operations (under strict confidentiality agreements)",
    ],
  },
  {
    icon: Lock,
    color: "text-red-600",
    bg: "bg-red-50",
    number: "05",
    title: "Data Security",
    content:
      "We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes:",
    bullets: [
      "Encryption of data in transit and at rest",
      "Secure authentication and access controls",
      "Regular security audits and updates",
      "Limited access to personal data on a need-to-know basis",
    ],
  },
  {
    icon: UserCheck,
    color: "text-teal-600",
    bg: "bg-teal-50",
    number: "06",
    title: "Your Rights",
    content: "You have the right to:",
    bullets: [
      "Access and review your personal information",
      "Request correction of inaccurate data",
      "Request deletion of your account and data",
      "Opt-out of location tracking (may limit app functionality)",
      "Withdraw consent for data processing",
    ],
  },
  {
    icon: Clock,
    color: "text-purple-600",
    bg: "bg-purple-50",
    number: "07",
    title: "Data Retention",
    content:
      "We retain your personal information for as long as necessary to provide our services and comply with legal obligations. Location data is typically retained for 30 days, while account information is retained until account deletion is requested.",
  },
  {
    icon: Baby,
    color: "text-pink-600",
    bg: "bg-pink-50",
    number: "08",
    title: "Children's Privacy",
    content:
      "Our services are intended for university students, staff, and authorized personnel. We do not knowingly collect personal information from children under 13 years of age.",
  },
  {
    icon: RefreshCw,
    color: "text-cyan-600",
    bg: "bg-cyan-50",
    number: "09",
    title: "Changes to This Policy",
    content:
      'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this policy periodically.',
  },
];

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isStandalone = location.pathname === "/privacy-policy";

  /* ── SEO: Privacy Policy page meta ── */
  useEffect(() => {
    updateSEO({
      title: "Privacy Policy — How SaferSavvy Protects Your Data",
      description:
        "Read SaferSavvy's Privacy Policy to learn how we collect, use, and protect your personal information including location data, student records, and account details.",
      canonical: "/privacy-policy",
      keywords: "SaferSavvy privacy policy, data protection, student data privacy, campus transport privacy",
      jsonLd: buildBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Privacy Policy", url: "/privacy-policy" },
      ]),
    });
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {isStandalone && <Navbar />}

      {/* Hero Section */}
      <div
        className={`relative bg-gradient-to-br from-primary-700 via-primary-600 to-primary-800 ${
          isStandalone ? "pt-32 pb-16" : "pt-8 pb-12"
        }`}
      >
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/5 rounded-full" />
          <div className="absolute bottom-0 -left-20 w-60 h-60 bg-white/5 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/[0.02] rounded-full" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 text-center">
          {!isStandalone && (
            <button
              onClick={() => navigate(-1)}
              className="absolute left-4 sm:left-6 top-0 flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          )}

          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            className="w-16 h-16 bg-white/10 backdrop-blur rounded-2xl flex items-center justify-center mx-auto mb-6"
          >
            <Shield className="w-8 h-8 text-white" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-3xl sm:text-4xl font-bold text-white mb-3"
          >
            Privacy Policy
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-white/70 text-sm sm:text-base max-w-xl mx-auto"
          >
            Your privacy is fundamental to everything we do. Learn how we
            collect, use, and protect your personal information.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-4 inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-2 rounded-full text-white/80 text-xs"
          >
            <Clock className="w-3.5 h-3.5" />
            Last Updated: January 2024
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="space-y-6">
          {sections.map((section, idx) => {
            const Icon = section.icon;
            return (
              <motion.div
                key={section.number}
                custom={idx}
                variants={fadeIn}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                {/* Section Header */}
                <div className="flex items-center gap-4 px-6 py-5 border-b border-gray-50">
                  <div
                    className={`w-11 h-11 ${section.bg} rounded-xl flex items-center justify-center flex-shrink-0`}
                  >
                    <Icon className={`w-5 h-5 ${section.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-300">
                        {section.number}
                      </span>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {section.title}
                      </h2>
                    </div>
                  </div>
                </div>

                {/* Section Body */}
                <div className="px-6 py-5">
                  {section.content && (
                    <p className="text-sm text-gray-600 leading-relaxed mb-4">
                      {section.content}
                    </p>
                  )}

                  {section.bullets && (
                    <ul className="space-y-2.5 ml-1">
                      {section.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-2 flex-shrink-0" />
                          <span className="text-sm text-gray-600">{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {section.subsections?.map((sub, sIdx) => (
                    <div
                      key={sIdx}
                      className={`${sIdx > 0 ? "mt-6 pt-5 border-t border-gray-50" : ""}`}
                    >
                      <h3 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        {sub.icon &&
                          React.createElement(sub.icon, {
                            className: "w-4 h-4 text-gray-400",
                          })}
                        {sub.subtitle}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed mb-3">
                        {sub.text}
                      </p>
                      {sub.bullets && (
                        <ul className="space-y-2 ml-1">
                          {sub.bullets.map((b, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <div className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-2 flex-shrink-0" />
                              <span className="text-sm text-gray-600">{b}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}

          {/* Contact Card */}
          <motion.div
            custom={sections.length}
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-8 text-center"
          >
            <div className="w-14 h-14 bg-white/10 backdrop-blur rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Mail className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              Questions About Your Privacy?
            </h3>
            <p className="text-white/70 text-sm mb-5 max-w-md mx-auto">
              If you have any questions about this Privacy Policy or our data
              practices, please don't hesitate to contact us.
            </p>
            <div className="inline-flex flex-col sm:flex-row gap-3">
              <a
                href="mailto:info@safersavvy.tech"
                className="bg-white text-primary-700 font-medium px-6 py-2.5 rounded-xl text-sm hover:bg-white/90 transition-colors inline-flex items-center justify-center gap-2"
              >
                <Mail className="w-4 h-4" />
                info@safersavvy.tech
              </a>
            </div>
            <p className="text-white/50 text-xs mt-4">
              University of Engineering and Technology, Lahore
            </p>
          </motion.div>
        </div>
      </div>

      {isStandalone && <Footer />}
    </div>
  );
};

export default PrivacyPolicy;
